/**
 * Landing bulk — Company Scope Impact: preview, already-assessed dialogs, task + poll.
 */

import { useCallback, useRef, useState } from "react";
import { useGuardedAsyncCallback } from "../../hooks/useGuardedAsyncCallback";
import { apiClient } from "../../services/apiClient";
import {
  getAssessmentTaskUuid,
  postSystemBundleTaskCompanyScope,
  postSystemBundleTaskPreview,
  type AssessmentRegulation,
} from "../../services/assessmentService";
import type { AiResultsModalRow } from "../../components/AssessmentCenterPage";
import { pollRegulationAssessmentStatusUntilDone } from "./regulationAssessmentPoll";

const MAPPING_NOT_CONFIGURED =
  "Process mapping is not configured. Please contact your admin to complete the setup.";

/** Preview API may return HTTP 200 with envelope status failed/error (no axios throw). */
export function isPreviewEnvelopeFailure(status: string | undefined): boolean {
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  return normalized === "failed" || normalized === "error";
}

export function getPreviewFailureMessage(
  message: string | null | undefined,
): string {
  const trimmed = typeof message === "string" ? message.trim() : "";
  return trimmed || MAPPING_NOT_CONFIGURED;
}

/** Cooldown after Company Scope flow ends — ignores rapid repeat clicks (debounce). */
const COMPANY_SCOPE_CLICK_DEBOUNCE_MS = 300;

function stripVersionPrefix(version: string | undefined): string | undefined {
  if (!version) return undefined;
  return version.startsWith("v") ? version.slice(1) : version;
}

function formatExecutionDateForGrid(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(/\s+/g, "-");
}

export async function fetchAssessmentRegulationsForIds(
  ids: string[],
  regulationVersionOverrides?: Record<string, string>,
): Promise<AssessmentRegulation[]> {
  const regulationPromises = ids.map(async (id) => {
    try {
      const overrideVersion = regulationVersionOverrides?.[id];
      const versionNum = stripVersionPrefix(overrideVersion);
      const url = versionNum
        ? `/regulations/${id}/version/${versionNum}`
        : `/regulations/${id}`;
      const result = await apiClient.get(url);
      if (result.status === "success" && result.data) {
        const docs = result.data.associated_documents || [];
        const documents = docs.map(
          (d: {
            file_path?: string;
            path?: string;
            name?: string;
            guid?: string;
            id?: string;
          }) => ({
            file_path: d.file_path ?? d.path ?? "",
            name: d.name ?? "",
            guid: d.guid ?? d.id ?? "",
          }),
        );
        return {
          id: result.data.regulation_id || result.data.id || id,
          version: result.data.version || versionNum || "1.0",
          title: result.data.title || `Regulation ${id}`,
          documents,
        } as AssessmentRegulation;
      }
      return null;
    } catch {
      return null;
    }
  });
  const regulationResults = await Promise.all(regulationPromises);
  return regulationResults.filter((reg): reg is AssessmentRegulation => reg !== null);
}

function regulationKey(r: AssessmentRegulation): string {
  return `${r.id}:${r.version}`;
}

/** Preview may return already-assessed rows outside the current batch — keep only current selection. */
export function filterAlreadyRunRegulationsForSelection(
  selection: AssessmentRegulation[],
  alreadyRun: AssessmentRegulation[],
): AssessmentRegulation[] {
  if (selection.length === 0 || alreadyRun.length === 0) return [];
  const keys = new Set(selection.map(regulationKey));
  return alreadyRun.filter((r) => keys.has(regulationKey(r)));
}

function filterOutAlreadyRun(
  all: AssessmentRegulation[],
  already: AssessmentRegulation[],
): AssessmentRegulation[] {
  const keys = new Set(already.map(regulationKey));
  return all.filter((r) => !keys.has(regulationKey(r)));
}

function allSelectedAlreadyRun(
  selectedRegs: AssessmentRegulation[],
  alreadyRun: AssessmentRegulation[],
): boolean {
  if (selectedRegs.length === 0 || alreadyRun.length === 0) return false;
  const keys = new Set(alreadyRun.map(regulationKey));
  return selectedRegs.every((r) => keys.has(regulationKey(r)));
}

function buildCompanyScopeRows(
  regulations: AssessmentRegulation[],
  status: string,
  taskUuid?: string,
): AiResultsModalRow[] {
  const rawStatus =
    status === "Running"
      ? "running"
      : status === "Completed"
        ? "success"
        : status.trim().toLowerCase();
  const executedDate = formatExecutionDateForGrid(new Date());

  return regulations.map((regulation) => ({
    id: `${regulation.id}:${regulation.version}:company`,
    regulationId: regulation.id,
    regulation: regulation.title || regulation.id,
    version: regulation.version,
    executedDate,
    rawStatus,
    status,
    executedBy: "Current User",
    assessmentType: "system",
    impactedConcepts: "—",
    impactLevel: status === "Running" ? "Pending" : "—",
    reportUrl: null,
    taskUuid: taskUuid ?? "",
  }));
}

export interface UseCompanyScopeImpactFlowParams {
  selectedIds: string[];
  regulationVersionOverrides?: Record<string, string>;
  maxRegulationsPerBatch: number;
  onShowSnackbar: (state: {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }) => void;
  openAiResultsModalWithRows: (rows: AiResultsModalRow[]) => void;
  /** Updates AI Results modal rows while polling `regulation-assessment-status`. */
  updateAiResultsModalRows: (
    rowsOrUpdater:
      | AiResultsModalRow[]
      | ((previous: AiResultsModalRow[]) => AiResultsModalRow[]),
  ) => void;
  /** Drives AI Results modal DataGrid loading overlay during regulation-status polling. */
  setAiResultsTableLoading: (loading: boolean) => void;
  refreshAssessmentCenter: () => void;
}

export function useCompanyScopeImpactFlow({
  selectedIds,
  regulationVersionOverrides,
  maxRegulationsPerBatch,
  onShowSnackbar,
  openAiResultsModalWithRows,
  updateAiResultsModalRows,
  setAiResultsTableLoading,
  refreshAssessmentCenter,
}: UseCompanyScopeImpactFlowParams) {
  const [partialOpen, setPartialOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  /** Regulation ids in the Company Scope flow (preview / POST / poll) — row icons load per id only. */
  const [companyScopeLoadingRegulationIds, setCompanyScopeLoadingRegulationIds] =
    useState<string[]>([]);
  const companyScopeFlowLoading = companyScopeLoadingRegulationIds.length > 0;
  const [pendingContext, setPendingContext] = useState<{
    regulations: AssessmentRegulation[];
    alreadyRun: AssessmentRegulation[];
    selectedTotal: number;
  } | null>(null);

  /** When true, `pollRegulationAssessmentStatus` stops on next check (e.g. user closed AI Results modal). */
  const pollCancelledRef = useRef(false);

  const cancelRegulationStatusPolling = useCallback(() => {
    pollCancelledRef.current = true;
    setAiResultsTableLoading(false);
    setCompanyScopeLoadingRegulationIds([]);
  }, [setAiResultsTableLoading]);

  const pollRegulationAssessmentStatus = useCallback(
    async (taskUuid: string, regulations: AssessmentRegulation[]) => {
      pollCancelledRef.current = false;
      try {
        await pollRegulationAssessmentStatusUntilDone({
          taskUuid,
          regulations,
          by: "system",
          updateAiResultsModalRows,
          setAiResultsTableLoading,
          refreshAssessmentCenter,
          isCancelled: () => pollCancelledRef.current,
        });
      } finally {
        setCompanyScopeLoadingRegulationIds([]);
      }
    },
    [
      refreshAssessmentCenter,
      setAiResultsTableLoading,
      updateAiResultsModalRows,
    ],
  );

  const runCompanyScopeTask = useCallback(
    async (regulations: AssessmentRegulation[]) => {
      if (regulations.length === 0) return;
      setCompanyScopeLoadingRegulationIds(regulations.map((r) => r.id));
      try {
        const response = await postSystemBundleTaskCompanyScope({
          regulations,
          by: "user",
        });
        const taskUuid = getAssessmentTaskUuid(response);
        openAiResultsModalWithRows(
          buildCompanyScopeRows(regulations, "Running", taskUuid),
        );
        onShowSnackbar({
          open: true,
          message: `Company Scope Impact assessment started for ${regulations.length} regulation(s).`,
          severity: "success",
        });
        if (taskUuid) {
          void pollRegulationAssessmentStatus(taskUuid, regulations).catch(() => {
            refreshAssessmentCenter();
            setCompanyScopeLoadingRegulationIds([]);
          });
        } else {
          setCompanyScopeLoadingRegulationIds([]);
          refreshAssessmentCenter();
        }
      } catch (error) {
        setCompanyScopeLoadingRegulationIds([]);
        onShowSnackbar({
          open: true,
          message:
            error instanceof Error
              ? error.message
              : "Failed to start Company Scope Impact assessment.",
          severity: "error",
        });
      }
    },
    [
      onShowSnackbar,
      openAiResultsModalWithRows,
      pollRegulationAssessmentStatus,
      refreshAssessmentCenter,
    ],
  );

  const openHistoricalCompletedModal = useCallback(
    (regulations: AssessmentRegulation[]) => {
      openAiResultsModalWithRows(buildCompanyScopeRows(regulations, "Completed"));
      refreshAssessmentCenter();
    },
    [openAiResultsModalWithRows, refreshAssessmentCenter],
  );

  const closeDialogs = useCallback(() => {
    setPartialOpen(false);
    setAllOpen(false);
    setPendingContext(null);
  }, []);

  const startCompanyScopeImpactInner = useCallback(
    async (overrideIds?: string[]) => {
    const ids = overrideIds ?? selectedIds;
    if (ids.length === 0) {
      onShowSnackbar({
        open: true,
        message: "Please select at least one regulation.",
        severity: "error",
      });
      return;
    }
    if (ids.length > maxRegulationsPerBatch) {
      onShowSnackbar({
        open: true,
        message: `Please select at most ${maxRegulationsPerBatch} regulations.`,
        severity: "error",
      });
      return;
    }

    setCompanyScopeLoadingRegulationIds(ids);
    let retainRowLoadingUntilPoll = false;
    try {
      const regulations = await fetchAssessmentRegulationsForIds(
        ids,
        regulationVersionOverrides,
      );
      if (regulations.length === 0) {
        onShowSnackbar({
          open: true,
          message: "Failed to load regulation details.",
          severity: "error",
        });
        return;
      }

      let preview;
      try {
        preview = await postSystemBundleTaskPreview({ regulations, by: "user" });
      } catch (error: unknown) {
        const raw =
          error && typeof error === "object" && "message" in error
            ? String((error as { message?: string }).message)
            : String(error);
        const lower = raw.toLowerCase();
        if (
          lower.includes("mapping") ||
          lower.includes("process mapping") ||
          raw.includes("Process mapping")
        ) {
          onShowSnackbar({
            open: true,
            message: MAPPING_NOT_CONFIGURED,
            severity: "error",
          });
          return;
        }
        onShowSnackbar({
          open: true,
          message: raw || "Preview failed.",
          severity: "error",
        });
        return;
      }

      if (isPreviewEnvelopeFailure(preview.status)) {
        onShowSnackbar({
          open: true,
          message: getPreviewFailureMessage(preview.message),
          severity: "error",
        });
        return;
      }

      const alreadyRunRaw = (preview.data?.already_run_regulations ??
        []) as AssessmentRegulation[];
      const alreadyRun = filterAlreadyRunRegulationsForSelection(
        regulations,
        alreadyRunRaw,
      );
      const selectedTotal = regulations.length;
      const xCount = alreadyRun.length;

      if (xCount === 0) {
        retainRowLoadingUntilPoll = true;
        await runCompanyScopeTask(regulations);
        return;
      }

      const fullyAlready = allSelectedAlreadyRun(regulations, alreadyRun);

      const payload = {
        regulations,
        alreadyRun,
        selectedTotal,
      };
      setPendingContext(payload);

      if (fullyAlready) {
        setAllOpen(true);
        return;
      }

      setPartialOpen(true);
    } catch (error) {
      onShowSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Company Scope preview failed.",
        severity: "error",
      });
    } finally {
      if (!retainRowLoadingUntilPoll) {
        setCompanyScopeLoadingRegulationIds([]);
      }
    }
  },
  [
    maxRegulationsPerBatch,
    onShowSnackbar,
    regulationVersionOverrides,
    runCompanyScopeTask,
    selectedIds,
  ],
);

  const startCompanyScopeImpact = useGuardedAsyncCallback(
    startCompanyScopeImpactInner,
    { cooldownMs: COMPANY_SCOPE_CLICK_DEBOUNCE_MS },
  );

  const handlePartialRunAnywayInner = useCallback(async () => {
    const ctx = pendingContext;
    closeDialogs();
    if (!ctx) return;
    setCompanyScopeLoadingRegulationIds(ctx.regulations.map((r) => r.id));
    await runCompanyScopeTask(ctx.regulations);
  }, [closeDialogs, pendingContext, runCompanyScopeTask]);

  const handlePartialSkipInner = useCallback(async () => {
    const ctx = pendingContext;
    closeDialogs();
    if (!ctx) return;
    const remaining = filterOutAlreadyRun(ctx.regulations, ctx.alreadyRun);
    if (remaining.length === 0) {
      openHistoricalCompletedModal(ctx.regulations);
      return;
    }
    setCompanyScopeLoadingRegulationIds(remaining.map((r) => r.id));
    await runCompanyScopeTask(remaining);
  }, [closeDialogs, openHistoricalCompletedModal, pendingContext, runCompanyScopeTask]);

  const handleAllRunAnywayInner = useCallback(async () => {
    const ctx = pendingContext;
    closeDialogs();
    if (!ctx) return;
    setCompanyScopeLoadingRegulationIds(ctx.regulations.map((r) => r.id));
    await runCompanyScopeTask(ctx.regulations);
  }, [closeDialogs, pendingContext, runCompanyScopeTask]);

  const handlePartialRunAnyway = useGuardedAsyncCallback(handlePartialRunAnywayInner, {
    cooldownMs: COMPANY_SCOPE_CLICK_DEBOUNCE_MS,
  });
  const handlePartialSkip = useGuardedAsyncCallback(handlePartialSkipInner, {
    cooldownMs: COMPANY_SCOPE_CLICK_DEBOUNCE_MS,
  });
  const handleAllRunAnyway = useGuardedAsyncCallback(handleAllRunAnywayInner, {
    cooldownMs: COMPANY_SCOPE_CLICK_DEBOUNCE_MS,
  });

  /** Optional: open completed-state modal without creating a task (all already done — poll-only when task uuid exists). */
  const handleAllCancelGoHistorical = useCallback(() => {
    const ctx = pendingContext;
    closeDialogs();
    if (!ctx) return;
    openHistoricalCompletedModal(ctx.regulations);
  }, [closeDialogs, openHistoricalCompletedModal, pendingContext]);

  return {
    startCompanyScopeImpact,
    cancelRegulationStatusPolling,
    companyScopeFlowLoading,
    companyScopeLoadingRegulationIds,
    partialDialogOpen: partialOpen,
    allDialogOpen: allOpen,
    pendingContext,
    closeCompanyScopeDialogs: closeDialogs,
    handlePartialRunAnyway,
    handlePartialSkip,
    handleAllRunAnyway,
    /** Use when product prefers showing historical rows without re-run from the “all assessed” dialog. */
    handleAllShowHistoricalOnly: handleAllCancelGoHistorical,
  };
}
